import { useEffect, useState, type ReactNode } from "react"
import Select from "~/ui-rtk/components/ui/controls/Select"
import useAccess from "~/ui-rtk/hooks/access"
import {
  useGetAdAccountsQuery,
  useGetCurrentAdAccountQuery,
  useSetAdAccountMutation,
} from "~/ui-rtk/api/campaignBlasterApi"

interface Props {
  title?: string
  selectedAdAccountId: string | null
  onSelect: (adAccountId: string) => void
  /**
   * Let the page through even when no Meta ad account is saved. For pages
   * that also work on another platform (AdBlaster with TikTok Ads
   * Management connected): a Meta account is then optional, not a gate.
   * The saved account, when one exists, is still resolved and reported via
   * `onSelect` so Meta-dependent tabs keep working.
   */
  allowWithoutAccount?: boolean
  children: ReactNode
}

export default function MetaAdAccountGate({
  title = "Select Meta Ad Account",
  selectedAdAccountId,
  onSelect,
  allowWithoutAccount = false,
  children,
}: Props) {
  const { isSuperAdmin } = useAccess()
  const { data: currentAccount, isLoading: isCurrentLoading } =
    useGetCurrentAdAccountQuery()
  const savedAdAccountId = currentAccount?.adAccountId ?? null
  const { data, isLoading, isError } = useGetAdAccountsQuery(undefined, {
    skip: !isSuperAdmin || !!savedAdAccountId,
  })
  const [saveAdAccount, { isLoading: isSaving }] = useSetAdAccountMutation()
  const [pendingId, setPendingId] = useState<string>("")
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    if (savedAdAccountId && !selectedAdAccountId) {
      onSelect(savedAdAccountId)
    }
  }, [savedAdAccountId, selectedAdAccountId, onSelect])

  useEffect(() => {
    if (!pendingId && data && data.length > 0) {
      setPendingId(data[0].id)
    }
  }, [data, pendingId])

  // Eager bypass: render children if there's already a saved value, even before
  // parent's selectedAdAccountId state has caught up via the useEffect above.
  // Same when the page doesn't need a Meta account at all.
  if (selectedAdAccountId || savedAdAccountId || allowWithoutAccount) {
    return <>{children}</>
  }

  if (isCurrentLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    )
  }

  if (!isSuperAdmin) {
    return (
      <div className="p-6 container mx-auto max-w-xl">
        <div className="bg-warning/10 border border-warning/30 rounded-lg p-4">
          <h2 className="text-base font-semibold text-warning mb-2">
            Ad account not configured
          </h2>
          <p className="text-sm text-basic-light/80">
            This workspace doesn&apos;t have a Meta ad account selected yet.
            Only the Marathon team can configure this — please reach out to them
            to get it set up.
          </p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    )
  }

  if (isError || !data || data.length === 0) {
    return (
      <div className="p-6 container mx-auto max-w-xl">
        <div className="bg-error/10 border border-error/30 rounded-lg p-4">
          <p className="text-error">
            No Meta ad accounts found. Please connect a Meta account with ad
            account access.
          </p>
        </div>
      </div>
    )
  }

  const options = data.map(a => ({
    label: `${a.name} (${a.id})`,
    value: a.id,
  }))

  return (
    <div className="p-6 container mx-auto max-w-xl">
      <h2 className="text-xl font-bold text-basic-white mb-2">{title}</h2>
      <p className="text-sm text-basic-light/60 mb-4">
        Choose which Meta ad account to use for this session.
      </p>
      <Select
        options={options}
        value={pendingId}
        onChange={e => setPendingId(e.target.value)}
      />
      {saveError && <p className="text-error text-sm mt-2">{saveError}</p>}
      <button
        className="btn btn-primary mt-4"
        disabled={!pendingId || isSaving}
        onClick={async () => {
          setSaveError(null)
          try {
            await saveAdAccount({ adAccountId: pendingId }).unwrap()
            onSelect(pendingId)
          } catch (err) {
            const message =
              (err as { data?: { message?: string } })?.data?.message ??
              "Failed to save ad account. Please try again."
            setSaveError(message)
          }
        }}
      >
        {isSaving ? "Saving..." : "Save"}
      </button>
    </div>
  )
}
